<script lang="ts" setup>
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router/composables'
import type { MemberPageConfig } from '../configs/types'
import { useUserStore } from '@/stores'
import { usePurchase, useSubscription } from '@/composables/payment'
import { tripartiteLoginPage } from '@/composables/useTripartiteLogin'
import { useElementClick } from '@/composables/tracks/useElementClick'
import { VIP_TYPE } from '@/constants'
import { resources as brandResources, theme } from '@/config/brands'

interface Props {
  config: MemberPageConfig
  inDrawer?: boolean
}

const props = defineProps<Props>()
const emit = defineEmits<{ (e: 'close'): void }>()
const router = useRouter()
const route = useRoute()

const userStore = useUserStore()
const { elementClick } = useElementClick()
const { subscribeProducts } = useSubscription('member')
const { templateProducts, loading } = usePurchase('member')

const topupShow = computed(() => {
  return templateProducts.value && templateProducts.value.length > 0
})

function login() {
  elementClick('login')
  tripartiteLoginPage('Profile')
}

function handleClose() {
  // 在抽屉中始终触发关闭，否则返回上一页
  if (props.inDrawer !== false)
    emit('close')
  else
    router.back()
}

function handleDrawerNavigate(name: string) {
  handleClose()

  if (route.name !== name)
    router.push({ name })
}

function handleTopupOrWelfare() {
  const target = topupShow.value ? 'pay' : 'welfare'
  handleClose()

  if (route.name !== target)
    router.push({ name: target })
}

const avatarBorderColor = props.config.theme.avatarBorder
const userDefaultAvatar = props.config.resources.userDefaultAvatar
const btnPrimaryBg = props.config.theme.btnPrimaryBg
const topUpBtnBg = props.config.theme.topUpBtnBg || 'linear-gradient(90deg, #C8E858 0%, #9AD844 100%)'
const vipBannerBg = props.config.theme.subscribeBg || 'linear-gradient(100deg, #2E2118 0%, #1E1510 60%, #2A1D15 100%)'
const vipTitleColor = props.config.theme.subscribeText || '#D4B896'
const vipSubtitleColor = props.config.theme.subscribeText2 || 'rgba(212, 184, 150, 0.55)'
const vipBtnBg = props.config.theme.vipBtnBg || 'linear-gradient(90deg, #D4B896 0%, #B89A78 100%)'

// 从品牌主题配置获取颜色
const bgGlobal = theme.bgGlobal
const bgCard = theme.bgCard
const borderPrimary = theme.borderPrimary
const textPrimary = theme.textPrimary
const textTertiary = theme.textTertiary
</script>

<template>
  <div class="page-container">
    <!-- Header -->
    <div class="header-bar">
      <div class="close-btn" @click="handleClose">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </div>
    </div>

    <!-- User Info -->
    <div class="user-section">
      <div class="flex items-center space-x-10px">
        <img
          v-if="userStore.$state.headImageUrl"
          class="w-48px h-48px rounded-full avatar-border object-cover"
          :src="userStore.$state.thirdHeadImgUrl"
          alt="avatar"
        >
        <img
          v-else
          class="w-48px h-48px rounded-full object-cover"
          :src="userDefaultAvatar"
          alt="avatar"
        >
        <div class="min-w-0">
          <p class="username">
            {{ userStore.$state.thirdNickName || userStore.$state.email || userStore.$state.memberName }}
          </p>
          <p v-if="userStore.$state.code" class="uid">
            UID {{ userStore.$state.code }}
          </p>
          <p v-if="userStore.$state.subscribe" class="flex items-center mt-4px">
            <img
              v-if="userStore.$state.subscribeType === VIP_TYPE.DISCOUNT"
              :src="config.resources.memberVipDiscount"
              class="w-40px h-13px"
            >
            <img v-else :src="config.resources.memberVip" class="w-40px h-13px">
          </p>
        </div>
      </div>
      <div v-if="false">
        <a class="login-btn" @click="login">
          {{ config.texts.signIn }}
        </a>
      </div>
    </div>

    <!-- Coin Card -->
    <div class="coin-card">
      <div class="flex items-center min-w-0">
        <div class="coin-icon-wrap">
          <img :src="brandResources?.coin || config.resources.memberVip" class="w-20px h-20px" alt="coin">
        </div>
        <strong class="coin-value">{{ userStore.$state.coinBalance }}</strong>
      </div>
      <div v-if="!loading" class="shrink-0 ml-10px">
        <div class="topup-btn" @click="elementClick('TopUp'); handleTopupOrWelfare()">
          {{ topupShow ? config.texts.topUp : config.texts.get }}
        </div>
      </div>
    </div>

    <!-- VIP Banner -->
    <div v-if="subscribeProducts?.length || userStore.$state.subscribe" class="vip-banner">
      <div class="flex-1 min-w-0">
        <div class="vip-title">
          <span v-if="!userStore.$state.subscribe">{{ config.texts.getVip }}</span>
          <span v-else>{{ config.texts.vipMembership }}</span>
        </div>
        <div v-if="!userStore.$state.subscribe" class="vip-subtitle">
          {{ config.texts.moreCoinsMoreBenefits }}
        </div>
        <div v-if="userStore.$state.subscribeEndDate" class="vip-subtitle">
          {{ config.texts.expires }} {{ $d(new Date(userStore.$state.subscribeEndDate), 'short') }}
        </div>
      </div>
      <div v-if="!userStore.$state.subscribe" class="shrink-0 ml-10px">
        <div class="vip-btn" @click="elementClick('Details'); handleDrawerNavigate('subscribe')">
          {{ config.texts.details }}
        </div>
      </div>
    </div>

    <!-- Menu Group 1 -->
    <div class="menu-card">
      <div class="menu-item" @click="handleDrawerNavigate('welfare')">
        <div class="flex items-center space-x-10px min-w-0">
          <div class="menu-icon" style="background: rgba(255, 107, 107, 0.12); color: #FF8A8A;">
            <i-icon-park-outline-gift class="text-14px" />
          </div>
          <p class="menu-label">
            {{ $t('RewardsTab') }}
          </p>
        </div>
        <i-icon-park-outline-right class="menu-arrow" />
      </div>

      <div class="menu-item border-t" @click="handleDrawerNavigate('history')">
        <div class="flex items-center space-x-10px min-w-0">
          <div class="menu-icon" style="background: rgba(78, 205, 196, 0.12); color: #6EDDD4;">
            <i-icon-park-outline-bookmark class="text-14px" />
          </div>
          <p class="menu-label">
            {{ $t('LibraryTab') }}
          </p>
        </div>
        <i-icon-park-outline-right class="menu-arrow" />
      </div>
    </div>

    <!-- Menu Group 2 -->
    <div class="menu-card">
      <div class="menu-item" @click="handleDrawerNavigate('recharge')">
        <div class="flex items-center space-x-10px min-w-0">
          <div class="menu-icon" style="background: rgba(102, 126, 234, 0.12); color: #8A9CF0;">
            <i-icon-park-outline-transaction class="text-14px" />
          </div>
          <p class="menu-label">
            {{ config.texts.transactionHistory }}
          </p>
        </div>
        <i-icon-park-outline-right class="menu-arrow" />
      </div>

      <div class="menu-item border-t" @click="handleDrawerNavigate('consume')">
        <div class="flex items-center space-x-10px min-w-0">
          <div class="menu-icon" style="background: rgba(240, 147, 251, 0.12); color: #F5A8F0;">
            <i-icon-park-outline-unlock class="text-14px" />
          </div>
          <p class="menu-label">
            {{ config.texts.chaptersUnlocked }}
          </p>
        </div>
        <i-icon-park-outline-right class="menu-arrow" />
      </div>

      <div class="menu-item border-t" @click="elementClick('contact_us'); handleDrawerNavigate('contact-us')">
        <div class="flex items-center space-x-10px min-w-0">
          <div class="menu-icon" style="background: rgba(79, 172, 254, 0.12); color: #7AC2FE;">
            <i-icon-park-outline-customer class="text-14px" />
          </div>
          <p class="menu-label">
            {{ config.texts.contactUs }}
          </p>
        </div>
        <i-icon-park-outline-right class="menu-arrow" />
      </div>

      <div class="menu-item border-t" @click="elementClick('setting'); handleDrawerNavigate('setting')">
        <div class="flex items-center space-x-10px min-w-0">
          <div class="menu-icon" style="background: rgba(67, 233, 123, 0.12); color: #6EEFA0;">
            <i-icon-park-outline-setting-one class="text-14px" />
          </div>
          <p class="menu-label">
            {{ config.texts.setting }}
          </p>
        </div>
        <i-icon-park-outline-right class="menu-arrow" />
      </div>
    </div>

    <div class="h-20px" />
  </div>
</template>

<style scoped>
.page-container {
  width: 100%;
  min-height: 100vh;
  background: v-bind(bgGlobal);
  padding: 0 12px 16px;
  box-sizing: border-box;
}

.header-bar {
  display: flex;
  align-items: center;
  height: 44px;
}

.close-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: v-bind(textTertiary);
  cursor: pointer;
}

.user-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 0 20px;
}

.avatar-border {
  border: 2px solid v-bind(avatarBorderColor);
}

.username {
  font-weight: 600;
  font-size: 16px;
  color: v-bind(textPrimary);
  line-height: 22px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 140px;
}

.uid {
  font-size: 11px;
  color: v-bind(textTertiary);
  line-height: 15px;
  margin-top: 2px;
}

.login-btn {
  display: inline-block;
  padding: 6px 14px;
  font-size: 12px;
  font-weight: 500;
  color: v-bind(textPrimary);
  background: v-bind(btnPrimaryBg);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 20px;
  cursor: pointer;
}

.coin-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: v-bind(bgCard);
  border: 1px solid v-bind(borderPrimary);
  border-radius: 16px;
  padding: 14px 14px;
  margin-bottom: 12px;
}

.coin-icon-wrap {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  flex-shrink: 0;
}

.coin-value {
  font-size: 22px;
  font-weight: 700;
  color: v-bind(textPrimary);
  letter-spacing: -0.5px;
}

.topup-btn,
.vip-btn {
  width: 80px;
  height: 32px;
  padding: 0 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  border-radius: 16px;
  cursor: pointer;
  white-space: nowrap;
}

.topup-btn {
  color: #FFFFFF !important;
  background: v-bind(topUpBtnBg);
}

.vip-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: v-bind(vipBannerBg);
  border: 1px solid v-bind(borderPrimary);
  border-radius: 16px;
  padding: 14px 14px;
  margin-bottom: 12px;
}

.vip-title {
  font-size: 15px;
  font-weight: 600;
  color: v-bind(vipTitleColor);
  line-height: 20px;
}

.vip-subtitle {
  font-size: 12px;
  color: v-bind(vipSubtitleColor);
  line-height: 16px;
  margin-top: 3px;
}

.vip-btn {
  color: #FFFFFF !important;
  background: v-bind(vipBtnBg);
}

.menu-card {
  background: v-bind(bgCard);
  border: 1px solid v-bind(borderPrimary);
  border-radius: 16px;
  padding: 2px 12px;
  margin-bottom: 12px;
}

.menu-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 13px 0;
  text-decoration: none;
}

.menu-item.border-t {
  border-top: 1px solid v-bind(borderPrimary);
}

.menu-icon {
  width: 26px;
  height: 26px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.menu-label {
  font-size: 14px;
  font-weight: 500;
  color: v-bind(textPrimary);
  line-height: 20px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.menu-arrow {
  font-size: 13px;
  color: v-bind(textTertiary);
  flex-shrink: 0;
  margin-left: 8px;
}
</style>
